import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

// URL de callback (respaldo por si el usuario hace click en el enlace del correo
// en lugar de ingresar el código). Se respeta el prefijo de despliegue __BASE_PATH__.
const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
const pathPrefix = basePath ? `/${basePath}` : '';
const recoveryRedirectTo = `${window.location.origin}${pathPrefix}/auth/callback?next=/auth/reset-password`;

function translateRecoveryError(message: string): string {
  if (message.includes('rate limit') || message.includes('too many')) {
    return 'Demasiados intentos. Espera un momento e inténtalo de nuevo.';
  }
  if (message.includes('expired') || message.includes('token')) {
    return 'El código no es válido o ya expiró. Solicita uno nuevo.';
  }
  return 'No se pudo completar la recuperación. Inténtalo de nuevo.';
}

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSendCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setNotice('');

    if (!isSupabaseConfigured || !supabase) {
      setError('La conexión con Supabase no está configurada.');
      return;
    }
    if (!email.trim()) {
      setError('Ingresa tu correo electrónico.');
      return;
    }

    setSubmitting(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: recoveryRedirectTo,
    });
    setSubmitting(false);

    if (err) {
      setError(translateRecoveryError(err.message));
      return;
    }

    setNotice('Te enviamos un código de recuperación de 6 dígitos a tu correo.');
    setStep('code');
  }

  async function handleVerifyCode(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setNotice('');

    if (!token.trim()) {
      setError('Ingresa el código de 6 dígitos.');
      return;
    }

    setSubmitting(true);
    const { error: err } = await supabase.auth.verifyOtp({
      email: email.trim(),
      token: token.trim(),
      type: 'recovery',
    });
    setSubmitting(false);

    if (err) {
      setError(translateRecoveryError(err.message));
      return;
    }

    navigate('/auth/reset-password', { replace: true });
  }

  async function handleResend() {
    setError('');
    setNotice('');
    if (!isSupabaseConfigured || !supabase) return;

    setSubmitting(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: recoveryRedirectTo,
    });
    setSubmitting(false);

    if (err) {
      setError(translateRecoveryError(err.message));
      return;
    }
    setNotice('Enviamos un nuevo código de recuperación a tu correo.');
  }

  return (
    <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Marca */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-accent-500 flex items-center justify-center mb-4">
            <i className="ri-heart-pulse-line text-white text-2xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-950 font-heading">Recuperar contraseña</h1>
          <p className="text-sm text-foreground-500 mt-1">Paticas con Propósito · Panel de administración</p>
        </div>

        {/* Tarjeta */}
        <div className="bg-white rounded-2xl border border-background-200 p-8">
          {step === 'email' ? (
            <>
              <h2 className="text-lg font-semibold text-foreground-900">Ingresa tu correo</h2>
              <p className="text-sm text-foreground-500 mt-1 mb-6">
                Te enviaremos un código de 6 dígitos para restablecer tu contraseña.
              </p>

              {error && (
                <div className="mb-5 flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
                  <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSendCode} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Correo electrónico
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@paticasconproposito.com"
                    className="w-full text-sm px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-300 transition"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full text-sm font-semibold py-2.5 px-4 rounded-lg bg-accent-500 text-white hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {submitting ? 'Enviando…' : 'Enviar código'}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold text-foreground-900">Verifica tu código</h2>
              <p className="text-sm text-foreground-500 mt-1 mb-6">
                Ingresa el código de 6 dígitos que enviamos a <strong>{email}</strong>.
              </p>

              {notice && (
                <div className="mb-5 flex items-start gap-2 text-sm bg-green-50 text-green-700 border border-green-200 rounded-lg px-3 py-2.5">
                  <i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0"></i>
                  <span>{notice}</span>
                </div>
              )}

              {error && (
                <div className="mb-5 flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
                  <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div>
                  <label htmlFor="token" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Código de recuperación
                  </label>
                  <input
                    id="token"
                    name="token"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={token}
                    onChange={(e) => setToken(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="000000"
                    className="w-full text-center text-xl tracking-[0.5em] px-4 py-3 rounded-lg border border-background-200 bg-background-50 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-300 transition"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full text-sm font-semibold py-2.5 px-4 rounded-lg bg-accent-500 text-white hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {submitting ? 'Verificando…' : 'Verificar código'}
                </button>
              </form>

              <div className="mt-5 flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={submitting}
                  className="text-accent-600 hover:text-accent-700 font-medium disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  Reenviar código
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('email');
                    setToken('');
                    setError('');
                    setNotice('');
                  }}
                  className="text-foreground-500 hover:text-foreground-700 whitespace-nowrap cursor-pointer"
                >
                  Cambiar correo
                </button>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-foreground-500 hover:text-accent-600 transition-colors inline-flex items-center gap-1.5"
          >
            <i className="ri-arrow-left-line"></i>Volver al inicio de sesión
          </Link>
        </div>
      </div>
    </div>
  );
}